'use client';

import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { PromoToolSlot } from '@/types/amplience';
import { isScheduledItemExpired } from '@/utility/isScheduledItemExpired';
import {
  ActiveCoupon,
  CouponSource,
  addActiveCoupon,
  clearActiveCoupons,
  clearLeftoverActiveCoupons,
  getActiveCoupons,
} from '@/utility/promos';

export const promoConfigAtom = atom<PromoToolSlot | null>(
  (get) => get(overwrittenPromoConfigAtom) || get(cachedPromoConfigAtom)
);
export const isPromoExpiredAtom = atom<boolean>((get) => {
  const promoConfig = get(promoConfigAtom);
  return promoConfig ? isScheduledItemExpired(promoConfig.content._meta) : true;
});
export const overwrittenPromoConfigAtom = atom<PromoToolSlot | null>(null);
export const cachedPromoConfigAtom = atom<PromoToolSlot | null>(null);

export type PromoCode = {
  code: string;
  expires: Date | null;
};

// Mirrors the `activatedVoucher` cookie. Writes go through `addActiveCouponAtom`, which merges the
// coupon into the cookie and stores the resulting list here.
const activeCouponsStorageAtom = atomWithStorage<ActiveCoupon[]>('activeCoupons', [], {
  getItem: () => (typeof window === 'undefined' ? [] : getActiveCoupons()),
  setItem: () => {},
  removeItem: () => clearActiveCoupons(),
});

export const activeCouponsAtom = atom((get) => get(activeCouponsStorageAtom));

export const addActiveCouponAtom = atom(
  null,
  (
    _get,
    set,
    {
      code,
      source,
      expires,
      replace = false,
    }: { code: string; source: CouponSource; expires: Date | null; replace?: boolean }
  ) => {
    set(activeCouponsStorageAtom, addActiveCoupon(code, source, expires, { replace }));
  }
);

// Single-coupon mode of the multi-coupon A/B test: reset coupons left over from the variant.
export const clearLeftoverActiveCouponsAtom = atom(null, (_get, set) => {
  set(activeCouponsStorageAtom, clearLeftoverActiveCoupons());
});

export const isCurrentPromoActivatedAtom = atom((get) => {
  const code = get(promoConfigAtom)?.content['promo-tool'].campaignDetails.code;
  return Boolean(code) && get(activeCouponsAtom).some((c) => c.code === code);
});

export const initialPromoCountAtom = atom(0);

export const openPromoPopupAtom = atom(false);
